import { useState } from 'react'
import { Alert, App as AntdApp, Button, Form, Input, Space, Typography } from 'antd'
import { LockOutlined, SaveOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { validatePasswordStrength } from '@/utils/passwordRules'

const { Text, Title } = Typography

interface PasswordFormValues {
  oldPassword: string
  newPassword: string
  confirmPassword: string
}

export default function PasswordChangePage() {
  const navigate = useNavigate()
  const { message } = AntdApp.useApp()
  const [form] = Form.useForm<PasswordFormValues>()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const { actor, authLoading, mustChangePassword, changePassword } = useAuthStore()

  const submit = async () => {
    const values = await form.validateFields()
    setErrorMessage(null)
    try {
      await changePassword(values.oldPassword, values.newPassword)
      message.success('密码已更新')
      navigate('/dashboard', { replace: true })
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '密码更新失败')
    }
  }

  return (
    <div data-testid="password-change-page" style={{ maxWidth: 520 }}>
      <Space orientation="vertical" size={16} style={{ width: '100%' }}>
        <Space orientation="vertical" size={4}>
          <Title level={4} style={{ margin: 0 }}>修改密码</Title>
          <Text type="secondary">{actor}</Text>
        </Space>

        {mustChangePassword && (
          <Alert type="warning" showIcon message="当前账号需要先修改密码" />
        )}
        {errorMessage && (
          <Alert type="error" showIcon message={errorMessage} />
        )}

        <Form form={form} layout="vertical" onFinish={submit}>
          <Form.Item
            name="oldPassword"
            label="当前密码"
            rules={[{ required: true, message: '请输入当前密码' }]}
          >
            <Input.Password
              autoComplete="current-password"
              prefix={<LockOutlined />}
            />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[{ required: true, message: '请输入新密码' }, { validator: (_, value) => validatePasswordStrength(value) }]}
          >
            <Input.Password
              autoComplete="new-password"
              prefix={<LockOutlined />}
            />
          </Form.Item>
          <Form.Item
            dependencies={['newPassword']}
            name="confirmPassword"
            label="确认新密码"
            rules={[
              { required: true, message: '请再次输入新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) return Promise.resolve()
                  return Promise.reject(new Error('两次输入的新密码不一致'))
                },
              }),
            ]}
          >
            <Input.Password
              autoComplete="new-password"
              prefix={<LockOutlined />}
            />
          </Form.Item>
          <Button
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={authLoading}
            type="primary"
          >
            保存密码
          </Button>
        </Form>
      </Space>
    </div>
  )
}
